import { useState, useRef, useEffect, type FormEvent, type KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import PhoneInput from '../base/PhoneInput';
import { useChat, CHAT_COMING_SOON, type ChatMessage } from '../../hooks/useChat';
import type { ChatProperty } from '../../services/rentalChatService';
import { getChatConfig } from '../../services/rentalChatService';
import { resolveApiBaseUrl } from '../../utils/apiClient';
import { useRevealOnScroll } from '../../hooks/useRevealOnScroll';
import { formatAmount, latnLocale } from '../../utils/numberFormat';

// API base URL for help desk form submission
const API_BASE_URL = resolveApiBaseUrl();

// Property card component for chat results
function PropertyCard({ property }: { property: ChatProperty; lang: 'en' | 'ar' }) {
  const price = formatAmount(property.price) || 'N/A';
  const currency = property.currency || 'SAR';

  return (
    <Link
      // Always use the UUID id — the property detail page resolves /api/properties/:id
      // through a UUID-only Zod schema, so a non-UUID slug 400s. Other surfaces
      // (Dashboard, Trusted Brokers cards) already link by id.
      to={`/property/${property.id}`}
      className="block bg-white rounded-xl border border-[var(--pv-border-soft)] shadow-sm hover:shadow-md transition-shadow overflow-hidden"
    >
      {property.imageUrl && (
        <div className="h-24 bg-gray-100 overflow-hidden">
          <img
            src={property.imageUrl}
            alt={property.title}
            className="w-full h-full object-cover"
            onError={(e) => {
              e.currentTarget.style.display = 'none';
            }}
          />
        </div>
      )}
      <div className="p-3">
        <h4 className="font-medium text-sm text-[var(--pv-color-text)] line-clamp-1">
          {property.title}
        </h4>
        <div className="mt-1 flex items-center gap-2 text-xs text-[var(--pv-color-text-muted)]">
          <span className="font-semibold text-[var(--pv-color-primary)]">
            {price} {currency}
          </span>
          <span>•</span>
          <span>{property.city}</span>
        </div>
        <div className="mt-1 flex items-center gap-3 text-xs text-[var(--pv-color-text-muted)]">
          {property.bedrooms !== undefined && (
            <span className="flex items-center gap-1">
              <i className="ri-hotel-bed-line"></i>
              {property.bedrooms}
            </span>
          )}
          {property.bathrooms !== undefined && (
            <span className="flex items-center gap-1">
              <i className="ri-drop-line"></i>
              {property.bathrooms}
            </span>
          )}
          {property.propertyType && (
            <span className="capitalize">{property.propertyType.toLowerCase()}</span>
          )}
        </div>
      </div>
    </Link>
  );
}

// Format relative time
function formatRelativeTime(date: Date, lang: 'en' | 'ar'): string {
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);

  if (diffMins < 1) {
    return lang === 'ar' ? 'الآن' : 'Just now';
  }
  if (diffMins < 60) {
    return lang === 'ar' ? `منذ ${diffMins} دقيقة` : `${diffMins}m ago`;
  }
  if (diffHours < 24) {
    return lang === 'ar' ? `منذ ${diffHours} ساعة` : `${diffHours}h ago`;
  }
  return date.toLocaleDateString(latnLocale(lang === 'ar' ? 'ar-SA' : 'en-US'));
}

// Message bubble component
function MessageBubble({
  message,
  lang,
  onSuggestionClick,
  onActionClick,
}: {
  message: ChatMessage;
  lang: 'en' | 'ar';
  onSuggestionClick: (suggestion: string) => void;
  onActionClick: (action: ChatMessage['action']) => void;
}) {
  const isBot = message.sender === 'bot';
  const isRTL = lang === 'ar';

  return (
    <div
      className={[
        'flex w-full gap-2',
        isBot ? '' : 'flex-row-reverse',
      ].join(' ')}
    >
      {/* Avatar */}
      {isBot && (
        <div className="flex-shrink-0">
          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-[var(--pv-color-primary)] text-white shadow-lg">
            <i className="ri-robot-2-line text-sm"></i>
          </div>
        </div>
      )}

      {/* Message content */}
      <div
        className={[
          'max-w-[80%] space-y-2',
          isBot ? '' : 'items-end',
        ].join(' ')}
      >
        <div
          className={[
            'rounded-2xl px-4 py-3 text-sm leading-relaxed shadow-sm',
            isBot
              ? 'rounded-tl-sm bg-white text-[var(--pv-color-text)] border border-[var(--pv-border-soft)]'
              : 'rounded-tr-sm bg-[var(--pv-color-primary)] text-white',
          ].join(' ')}
          dir={isRTL ? 'rtl' : 'ltr'}
        >
          <p className="whitespace-pre-wrap">{message.content}</p>
        </div>

        {/* Suggestions */}
        {isBot && message.suggestions && message.suggestions.length > 0 && (
          <div className="flex flex-wrap gap-2 pt-1">
            {message.suggestions.map((suggestion, index) => (
              <button
                key={index}
                onClick={() => onSuggestionClick(suggestion)}
                className="rounded-full border border-[var(--pv-border-soft)] bg-white/80 px-3 py-1.5 text-xs text-[var(--pv-color-text-muted)] shadow-sm transition-all hover:border-[var(--pv-color-primary)] hover:bg-[rgba(0,77,64,0.08)] hover:text-[var(--pv-color-primary)]"
              >
                {suggestion}
              </button>
            ))}
          </div>
        )}

        {/* Property cards */}
        {isBot && message.properties && message.properties.length > 0 && (
          <div className="mt-3 space-y-2 max-w-full overflow-hidden">
            <p className="text-xs text-[var(--pv-color-text-muted)] font-medium">
              {lang === 'ar'
                ? `عقارات مقترحة (${message.properties.length})`
                : `Suggested properties (${message.properties.length})`}
            </p>
            <div className="grid grid-cols-1 gap-2">
              {message.properties.slice(0, 3).map((property) => (
                <PropertyCard key={property.id} property={property} lang={lang} />
              ))}
            </div>
            {message.properties.length > 3 && (
              <button
                onClick={() => onActionClick({ type: 'search', payload: {} })}
                className="w-full text-xs text-[var(--pv-color-primary)] hover:underline py-1"
              >
                {lang === 'ar'
                  ? `عرض ${message.properties.length - 3} عقارات أخرى`
                  : `View ${message.properties.length - 3} more properties`}
              </button>
            )}
          </div>
        )}

        {/* Action button */}
        {isBot && message.action && message.action.type !== 'none' && (
          <button
            onClick={() => onActionClick(message.action)}
            className="mt-2 flex items-center gap-2 rounded-xl bg-[rgba(0,77,64,0.1)] px-4 py-2 text-xs font-medium text-[var(--pv-color-primary)] transition-all hover:bg-[rgba(0,77,64,0.2)]"
          >
            <i className={[
              message.action.type === 'navigate' ? 'ri-arrow-right-line' : 'ri-search-line',
              isRTL ? 'rotate-180' : '',
            ].join(' ')}></i>
            <span>
              {message.action.type === 'navigate'
                ? (lang === 'ar' ? 'انتقل الآن' : 'Go there')
                : (lang === 'ar' ? 'بحث' : 'Search')}
            </span>
          </button>
        )}

        {/* Timestamp */}
        <p className={[
          'text-[10px] text-[var(--pv-color-text-muted)]/60 px-1',
          isBot ? '' : 'text-right',
        ].join(' ')}>
          {formatRelativeTime(message.timestamp, lang)}
        </p>
      </div>
    </div>
  );
}

// Typing indicator
function TypingIndicator(_props: { lang: 'en' | 'ar' }) {
  return (
    <div className="flex items-center gap-2">
      <div className="flex h-8 w-8 items-center justify-center rounded-full bg-[var(--pv-color-primary)] text-white shadow-lg">
        <i className="ri-robot-2-line text-sm"></i>
      </div>
      <div className="flex items-center gap-1 rounded-2xl rounded-tl-sm border border-[var(--pv-border-soft)] bg-white px-4 py-3 shadow-sm">
        <span className="h-2 w-2 animate-pulse rounded-full bg-[var(--pv-color-primary)]/60 motion-reduce:animate-none"></span>
        <span className="h-2 w-2 animate-pulse rounded-full bg-[var(--pv-color-primary)]/60 [animation-delay:0.2s] motion-reduce:animate-none"></span>
        <span className="h-2 w-2 animate-pulse rounded-full bg-[var(--pv-color-primary)]/60 [animation-delay:0.4s] motion-reduce:animate-none"></span>
      </div>
    </div>
  );
}

// Help Desk Fallback Form
function HelpDeskForm({
  lang,
  onBack,
  onSubmitSuccess,
}: {
  lang: 'en' | 'ar';
  onBack: () => void;
  onSubmitSuccess: () => void;
}) {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const isRTL = lang === 'ar';

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      const response = await fetch(`${API_BASE_URL}/inquiries/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          source: 'chat_helpdesk',
          pageUrl: window.location.pathname,
        }),
      });

      if (response.ok) {
        onSubmitSuccess();
      } else {
        const data = await response.json();
        setError(data.message || (lang === 'ar' ? 'فشل الإرسال. يرجى المحاولة مرة أخرى.' : 'Failed to submit. Please try again.'));
      }
    } catch {
      setError(lang === 'ar' ? 'خطأ في الاتصال. يرجى المحاولة مرة أخرى.' : 'Connection error. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="p-4 space-y-4" dir={isRTL ? 'rtl' : 'ltr'}>
      <div className="text-center">
        <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-amber-100">
          <i className="ri-customer-service-line text-2xl text-amber-600"></i>
        </div>
        <h4 className="font-semibold text-[var(--pv-color-text)]">
          {lang === 'ar' ? 'مكتب المساعدة' : 'Help Desk'}
        </h4>
        <p className="mt-1 text-xs text-[var(--pv-color-text-muted)]">
          {lang === 'ar'
            ? 'الدردشة الآلية غير متوفرة حالياً. يرجى ملء النموذج وسنتواصل معك قريباً.'
            : 'AI chat is currently unavailable. Please fill out this form and we\'ll get back to you shortly.'}
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-3">
        <div>
          <input
            type="text"
            required
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            placeholder={lang === 'ar' ? 'الاسم *' : 'Name *'}
            className="w-full rounded-xl border border-[var(--pv-border-soft)] bg-white px-3 py-2 text-sm text-[var(--pv-color-text)] placeholder-[var(--pv-color-text-muted)]/60 outline-none transition focus:border-[var(--pv-color-primary)]"
          />
        </div>
        <div>
          <input
            type="email"
            required
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            placeholder={lang === 'ar' ? 'البريد الإلكتروني *' : 'Email *'}
            className="w-full rounded-xl border border-[var(--pv-border-soft)] bg-white px-3 py-2 text-sm text-[var(--pv-color-text)] placeholder-[var(--pv-color-text-muted)]/60 outline-none transition focus:border-[var(--pv-color-primary)]"
          />
        </div>
        <div>
          <PhoneInput
            value={formData.phone}
            onChange={(value) => setFormData({ ...formData, phone: value })}
            placeholder={lang === 'ar' ? 'رقم الهاتف' : 'Phone (optional)'}
          />
        </div>
        <div>
          <select
            value={formData.subject}
            onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
            className="w-full rounded-xl border border-[var(--pv-border-soft)] bg-white px-3 py-2 text-sm text-[var(--pv-color-text)] outline-none transition focus:border-[var(--pv-color-primary)]"
          >
            <option value="">{lang === 'ar' ? 'اختر الموضوع' : 'Select subject'}</option>
            <option value="property_search">{lang === 'ar' ? 'البحث عن عقار' : 'Property search'}</option>
            <option value="viewing_request">{lang === 'ar' ? 'طلب معاينة' : 'Viewing request'}</option>
            <option value="pricing">{lang === 'ar' ? 'استفسار عن الأسعار' : 'Pricing inquiry'}</option>
            <option value="general">{lang === 'ar' ? 'استفسار عام' : 'General inquiry'}</option>
          </select>
        </div>
        <div>
          <textarea
            required
            value={formData.message}
            onChange={(e) => setFormData({ ...formData, message: e.target.value })}
            placeholder={lang === 'ar' ? 'رسالتك *' : 'Your message *'}
            rows={3}
            className="w-full rounded-xl border border-[var(--pv-border-soft)] bg-white px-3 py-2 text-sm text-[var(--pv-color-text)] placeholder-[var(--pv-color-text-muted)]/60 outline-none transition focus:border-[var(--pv-color-primary)] resize-none"
          />
        </div>

        {error && (
          <p className="text-xs text-red-600 bg-red-50 rounded-lg px-3 py-2">
            <i className="ri-error-warning-line mr-1"></i>
            {error}
          </p>
        )}

        <div className="flex gap-2">
          <button
            type="button"
            onClick={onBack}
            className="flex-1 rounded-xl border border-[var(--pv-border-soft)] bg-white px-4 py-2 text-sm font-medium text-[var(--pv-color-text-muted)] transition hover:bg-gray-50"
          >
            {lang === 'ar' ? 'رجوع' : 'Back'}
          </button>
          <button
            type="submit"
            disabled={isSubmitting}
            className="flex-1 rounded-xl bg-[var(--pv-color-primary)] px-4 py-2 text-sm font-medium text-white shadow-lg transition hover:shadow-xl disabled:opacity-50"
          >
            {isSubmitting ? (
              <span className="flex items-center justify-center gap-2">
                <i className="ri-loader-4-line animate-spin"></i>
                {lang === 'ar' ? 'جاري الإرسال...' : 'Sending...'}
              </span>
            ) : (
              lang === 'ar' ? 'إرسال' : 'Submit'
            )}
          </button>
        </div>
      </form>
    </div>
  );
}

// Help Desk Success Message
function HelpDeskSuccess({ lang, onClose }: { lang: 'en' | 'ar'; onClose: () => void }) {
  return (
    <div className="p-6 text-center space-y-4">
      <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-green-100">
        <i className="ri-check-line text-3xl text-green-600"></i>
      </div>
      <h4 className="font-semibold text-[var(--pv-color-text)]">
        {lang === 'ar' ? 'تم الإرسال بنجاح!' : 'Successfully Submitted!'}
      </h4>
      <p className="text-sm text-[var(--pv-color-text-muted)]">
        {lang === 'ar'
          ? 'شكراً لتواصلك معنا. سيقوم فريقنا بالرد عليك في أقرب وقت ممكن.'
          : 'Thank you for reaching out. Our team will get back to you as soon as possible.'}
      </p>
      <button
        onClick={onClose}
        className="w-full rounded-xl bg-[var(--pv-color-primary)] px-4 py-2 text-sm font-medium text-white shadow-lg transition hover:shadow-xl"
      >
        {lang === 'ar' ? 'إغلاق' : 'Close'}
      </button>
    </div>
  );
}

export default function ChatWidget() {
  const { t, i18n } = useTranslation('chat');
  const {
    isOpen,
    isMinimized,
    messages,
    isTyping,
    unreadCount,
    toggleChat,
    closeChat,
    minimizeChat,
    expandChat,
    sendMessage,
    handleSuggestionClick,
    handleActionClick,
    clearMessages,
  } = useChat();

  const [inputValue, setInputValue] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Help Desk state
  const [showHelpDesk, setShowHelpDesk] = useState(false);
  const [helpDeskSubmitted, setHelpDeskSubmitted] = useState(false);
  const [chatServiceStatus, setChatServiceStatus] = useState<'checking' | 'full' | 'limited'>('checking');

  const lang = i18n.language === 'ar' ? 'ar' : 'en';
  const isRTL = lang === 'ar';

  // Floating button stays hidden on first paint and slides in on scroll.
  const revealed = useRevealOnScroll();
  // One-time "How can I help?" nudge shown beside the button once it appears.
  const [hintDismissed, setHintDismissed] = useState(false);
  const showHint = revealed && !isOpen && !hintDismissed;

  // Check chat service status on mount
  useEffect(() => {
    // Bot is in "coming soon" mode — don't bother probing the backend.
    if (CHAT_COMING_SOON) return;
    const checkStatus = async () => {
      try {
        const config = await getChatConfig();
        setChatServiceStatus(config.status);
      } catch {
        setChatServiceStatus('limited');
      }
    };
    checkStatus();
  }, []);

  // Scroll to bottom when messages change
  useEffect(() => {
    if (messagesEndRef.current && isOpen && !isMinimized) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, isTyping, isOpen, isMinimized]);

  // Focus input when chat opens
  useEffect(() => {
    if (isOpen && !isMinimized && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 300);
    }
  }, [isOpen, isMinimized]);

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (inputValue.trim()) {
      sendMessage(inputValue);
      setInputValue('');
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  // ── "Coming soon" mode ──────────────────────────────────────────────────
  // The AI bot isn't launched yet. Render only the floating button with a
  // hover/focus tooltip; clicking does nothing and no chat panel exists.
  // Flip VITE_CHAT_COMING_SOON=false to restore the live bot below.
  if (CHAT_COMING_SOON) {
    const comingSoonLabel = t('comingSoon', { defaultValue: isRTL ? 'قريباً' : 'Coming soon' });
    return (
      <div
        className={[
          'group fixed z-[9998] flex flex-col items-center',
          isRTL ? 'left-4 sm:left-6' : 'right-4 sm:right-6',
          'bottom-[calc(1rem+var(--pv-float-offset,0px))] sm:bottom-[calc(1.5rem+var(--pv-float-offset,0px))]',
          'transition-all duration-300',
          revealed
            ? 'opacity-100 translate-y-0 pointer-events-auto'
            : 'opacity-0 translate-y-4 pointer-events-none',
        ].join(' ')}
        dir={isRTL ? 'rtl' : 'ltr'}
      >
        {/* Tooltip — revealed on hover / keyboard focus */}
        <div
          role="status"
          className="mb-3 whitespace-nowrap rounded-2xl bg-slate-900 px-4 py-2 text-xs font-medium text-white shadow-xl opacity-0 translate-y-1 pointer-events-none transition-all duration-200 group-hover:opacity-100 group-hover:translate-y-0 group-focus-within:opacity-100 group-focus-within:translate-y-0"
        >
          {comingSoonLabel}
        </div>

        {/* Disabled support button — shows the tooltip, never opens a chat */}
        <button
          type="button"
          aria-label={comingSoonLabel}
          aria-disabled="true"
          tabIndex={revealed ? 0 : -1}
          className="flex h-14 w-14 sm:h-16 sm:w-16 items-center justify-center rounded-full border-2 border-white/30 bg-[var(--pv-color-primary)] text-white shadow-2xl cursor-default transition-transform hover:scale-105"
        >
          <i className="ri-customer-service-2-line text-2xl sm:text-3xl"></i>
        </button>
      </div>
    );
  }

  return (
    <>
      {/* Chat Panel */}
      <div
        className={[
          'fixed z-[9999] transition-all duration-300 ease-out',
          isRTL ? 'left-4 sm:left-6' : 'right-4 sm:right-6',
          'bottom-[calc(5rem+var(--pv-float-offset,0px))] sm:bottom-[calc(6rem+var(--pv-float-offset,0px))]',
          isOpen ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4 pointer-events-none',
        ].join(' ')}
        style={{
          width: 'min(380px, calc(100vw - 32px))',
          height: isMinimized ? 'auto' : 'min(550px, calc(100vh - 140px))',
        }}
      >
        <div
          className={[
            'flex h-full flex-col overflow-hidden rounded-3xl border border-[var(--pv-border-soft)] bg-[var(--pv-color-surface)] shadow-2xl backdrop-blur-xl',
            isMinimized ? 'h-auto' : '',
          ].join(' ')}
          dir={isRTL ? 'rtl' : 'ltr'}
        >
          {/* Header */}
          <div className="flex items-center justify-between border-b border-[var(--pv-border-soft)] bg-[var(--pv-color-primary)] px-4 py-3 text-white">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-white/20 backdrop-blur">
                <i className="ri-customer-service-2-line text-xl"></i>
              </div>
              <div>
                <h3 className="font-semibold">{t('title')}</h3>
                <p className="text-xs text-white/80">
                  <span className="inline-block h-2 w-2 rounded-full bg-green-400 mr-1"></span>
                  {t('online')}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              {!isMinimized && (
                <button
                  onClick={() => clearMessages()}
                  className="rounded-lg p-2 text-white/80 transition hover:bg-white/20 hover:text-white"
                  title={lang === 'ar' ? 'مسح المحادثة' : 'Clear chat'}
                >
                  <i className="ri-delete-bin-line text-lg"></i>
                </button>
              )}
              <button
                onClick={isMinimized ? expandChat : minimizeChat}
                className="rounded-lg p-2 text-white/80 transition hover:bg-white/20 hover:text-white"
                aria-label={isMinimized ? t('expand') : t('minimize')}
              >
                <i className={isMinimized ? 'ri-fullscreen-line' : 'ri-subtract-line'}></i>
              </button>
              <button
                onClick={closeChat}
                className="rounded-lg p-2 text-white/80 transition hover:bg-white/20 hover:text-white"
                aria-label={t('close')}
              >
                <i className="ri-close-line text-lg"></i>
              </button>
            </div>
          </div>

          {/* Messages Area */}
          {!isMinimized && (
            <>
              {/* Help Desk Form — wrap in a scrollable container so the
                  Back/Submit buttons stay reachable on short viewports. The
                  chat-messages branch below already has its own scroll. */}
              {showHelpDesk ? (
                <div className="flex-1 overflow-y-auto">
                  {helpDeskSubmitted ? (
                    <HelpDeskSuccess
                      lang={lang}
                      onClose={() => {
                        setShowHelpDesk(false);
                        setHelpDeskSubmitted(false);
                      }}
                    />
                  ) : (
                    <HelpDeskForm
                      lang={lang}
                      onBack={() => setShowHelpDesk(false)}
                      onSubmitSuccess={() => setHelpDeskSubmitted(true)}
                    />
                  )}
                </div>
              ) : (
                <div className="flex-1 overflow-y-auto p-4 space-y-4">
                  {/* Service status banner */}
                  {chatServiceStatus === 'limited' && (
                    <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-center">
                      <p className="text-xs text-amber-700">
                        <i className="ri-information-line mr-1"></i>
                        {lang === 'ar'
                          ? 'الدردشة الذكية محدودة حالياً. يمكنك استخدام مكتب المساعدة بدلاً منها.'
                          : 'AI chat is currently limited. You can use the Help Desk instead.'}
                      </p>
                      <button
                        onClick={() => setShowHelpDesk(true)}
                        className="mt-2 text-xs font-medium text-amber-800 underline hover:no-underline"
                      >
                        {lang === 'ar' ? 'فتح مكتب المساعدة' : 'Open Help Desk'}
                      </button>
                    </div>
                  )}

                  {messages.length === 0 ? (
                    <div className="flex h-full flex-col items-center justify-center text-center p-4">
                      <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-[var(--pv-color-primary)]/15">
                        <i className="ri-chat-smile-3-line text-3xl text-[var(--pv-color-primary)]"></i>
                      </div>
                      <h4 className="mb-2 font-semibold text-[var(--pv-color-text)]">{t('subtitle')}</h4>
                      <p className="text-sm text-[var(--pv-color-text-muted)]">{t('welcomeOptions')}</p>
                    </div>
                  ) : (
                    messages.map((message) => (
                      <MessageBubble
                        key={message.id}
                        message={message}
                        lang={lang}
                        onSuggestionClick={handleSuggestionClick}
                        onActionClick={handleActionClick}
                      />
                    ))
                  )}
                  {isTyping && <TypingIndicator lang={lang} />}
                  <div ref={messagesEndRef} />
                </div>
              )}

              {/* Input Area - Hidden when showing Help Desk */}
              {!showHelpDesk && (
                <div className="border-t border-[var(--pv-border-soft)] bg-white/80 p-3 backdrop-blur">
                  <form onSubmit={handleSubmit} className="flex items-center gap-2">
                    <input
                      ref={inputRef}
                      type="text"
                      value={inputValue}
                      onChange={(e) => setInputValue(e.target.value)}
                      onKeyDown={handleKeyDown}
                      placeholder={t('placeholder')}
                      className="flex-1 rounded-xl border border-[var(--pv-border-soft)] bg-white px-4 py-2.5 text-sm text-[var(--pv-color-text)] placeholder-[var(--pv-color-text-muted)]/60 outline-none transition focus:border-[var(--pv-color-primary)] focus:ring-2 focus:ring-[var(--pv-color-primary)]/20"
                      dir="auto"
                    />
                    <button
                      type="submit"
                      disabled={!inputValue.trim()}
                      className={[
                        'flex h-10 w-10 items-center justify-center rounded-xl transition-all',
                        inputValue.trim()
                          ? 'bg-[var(--pv-color-primary)] text-white shadow-lg hover:shadow-xl hover:scale-105'
                          : 'bg-[var(--pv-border-soft)] text-[var(--pv-color-text-muted)] cursor-not-allowed',
                      ].join(' ')}
                      aria-label={t('send')}
                    >
                      <i className={[
                        'ri-send-plane-2-fill text-lg',
                        isRTL ? 'rotate-180' : '',
                      ].join(' ')}></i>
                    </button>
                  </form>
                  <div className="mt-2 flex items-center justify-between text-[10px] text-[var(--pv-color-text-muted)]/60">
                    <span>
                      <i className="ri-sparkling-line mr-1"></i>
                      {t('powered')}
                    </span>
                    <button
                      onClick={() => setShowHelpDesk(true)}
                      className="text-[var(--pv-color-primary)] hover:underline"
                    >
                      <i className="ri-customer-service-line mr-1"></i>
                      {lang === 'ar' ? 'مكتب المساعدة' : 'Help Desk'}
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* "How can I help?" nudge — appears beside the button on scroll so users
          notice live support exists; dismissible and never shown while open. */}
      {showHint && (
        <div
          className={[
            'fixed z-[9998] bottom-[calc(1.75rem+var(--pv-float-offset,0px))] sm:bottom-[calc(2.25rem+var(--pv-float-offset,0px))] flex items-center gap-2 rounded-2xl bg-white px-4 py-2.5 shadow-xl ring-1 ring-black/5 animate-fadeIn',
            isRTL ? 'left-20 sm:left-24' : 'right-20 sm:right-24',
          ].join(' ')}
          dir={isRTL ? 'rtl' : 'ltr'}
        >
          <button
            type="button"
            onClick={toggleChat}
            className="text-sm font-medium text-slate-700 hover:text-[var(--pv-color-primary)] whitespace-nowrap"
          >
            {t('helpHint', { defaultValue: isRTL ? 'كيف يمكنني مساعدتك؟' : 'How can I help?' })}
          </button>
          <button
            type="button"
            onClick={() => setHintDismissed(true)}
            className="text-slate-400 hover:text-slate-600"
            aria-label={t('close')}
          >
            <i className="ri-close-line text-base"></i>
          </button>
        </div>
      )}

      {/* Floating Button — hidden on first paint, slides in once scrolling starts */}
      <button
        onClick={toggleChat}
        className={[
          'fixed z-[9998] flex items-center justify-center rounded-full transition-all duration-300 border-2',
          isRTL ? 'left-4 sm:left-6' : 'right-4 sm:right-6',
          'bottom-[calc(1rem+var(--pv-float-offset,0px))] sm:bottom-[calc(1.5rem+var(--pv-float-offset,0px))]',
          revealed || isOpen
            ? 'opacity-100 translate-y-0 pointer-events-auto'
            : 'opacity-0 translate-y-4 pointer-events-none',
          isOpen
            ? 'h-12 w-12 bg-[var(--pv-color-primary)] text-white border-white/30 shadow-2xl hover:scale-105'
            : 'h-14 w-14 sm:h-16 sm:w-16 bg-[var(--pv-color-primary)] text-white border-white/30 shadow-2xl hover:shadow-2xl hover:scale-110',
        ].join(' ')}
        aria-hidden={!revealed && !isOpen}
        tabIndex={revealed || isOpen ? 0 : -1}
        aria-label={isOpen ? t('close') : t('openChat')}
      >
        <i className={[
          'transition-transform duration-300',
          isOpen ? 'ri-close-line text-2xl' : 'ri-customer-service-2-line text-2xl sm:text-3xl',
        ].join(' ')}></i>

        {/* Unread badge */}
        {!isOpen && unreadCount > 0 && (
          <span className="absolute -right-1 -top-1 flex h-5 w-5 items-center justify-center rounded-full bg-[#DF2F2B] text-xs font-bold text-white ring-2 ring-white">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>
    </>
  );
}
